import React, { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import { useToast } from '../ui/ToastProvider'

function unwrap(res) {
  return res?.data?.data !== undefined ? res.data.data : res.data
}

function filenameFromDisposition(header, fallback) {
  const value = String(header || '')
  const star = value.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)
  if (star?.[1]) {
    try { return decodeURIComponent(star[1]) } catch { return star[1] }
  }
  const plain = value.match(/filename="([^"]+)"/i)
  return plain?.[1] || fallback
}

export default function PolicyDeliveryBookDownloadTool() {
  const { show } = useToast()
  const [templates, setTemplates] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState('')
  const [comment, setComment] = useState('')
  const [sectionKeys, setSectionKeys] = useState([])

  const selected = useMemo(
    () => templates.find((template) => String(template.id) === String(selectedId)) || null,
    [templates, selectedId],
  )
  const selectedSections = useMemo(
    () => (Array.isArray(selected?.sections) ? selected.sections : []).filter((section) => section?.key && section?.label),
    [selected],
  )
  const canRender = Boolean(selected) && (!selectedSections.length || sectionKeys.length > 0)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const payload = unwrap(await api.get('/policy-delivery-books/templates'))
        if (cancelled) return
        const rows = Array.isArray(payload) ? payload : []
        setTemplates(rows)
        setSelectedId(rows[0]?.id || null)
      } catch {
        if (!cancelled) show('증권전달북 템플릿을 불러오지 못했습니다.', 'error')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [show])

  useEffect(() => {
    const sections = Array.isArray(selected?.sections) ? selected.sections : []
    setSectionKeys(sections.filter((section) => section.defaultIncluded !== false).map((section) => section.key).filter(Boolean))
  }, [selected])

  useEffect(() => () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl)
  }, [previewUrl])

  const clearPreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl)
    setPreviewUrl('')
  }

  const renderParams = () => ({
    params: {
      ...(comment.trim() ? { comment: comment.trim() } : {}),
      ...(selectedSections.length ? { sectionKeys: sectionKeys.join(',') } : {}),
    },
  })

  const toggleSection = (key) => {
    setSectionKeys((prev) => {
      const has = prev.includes(key)
      return has ? prev.filter((item) => item !== key) : [...prev, key]
    })
    clearPreview()
  }

  const preview = async () => {
    if (!canRender) return
    setDownloading(true)
    try {
      const res = await api.get(`/policy-delivery-books/templates/${selected.id}/preview`, {
        ...renderParams(),
        responseType: 'blob',
      })
      clearPreview()
      setPreviewUrl(window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })))
    } catch {
      show('미리보기 PDF 생성에 실패했습니다.', 'error')
    } finally {
      setDownloading(false)
    }
  }

  const download = async () => {
    if (!canRender) return
    setDownloading(true)
    try {
      const res = await api.get(`/policy-delivery-books/templates/${selected.id}/download`, {
        ...renderParams(),
        responseType: 'blob',
      })
      const blob = new Blob([res.data], { type: res?.headers?.['content-type'] || 'application/pdf' })
      const filename = filenameFromDisposition(res?.headers?.['content-disposition'], `${selected.title || '증권전달북'}.pdf`)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      show('증권전달북 PDF를 다운로드했습니다.', 'success')
    } catch {
      show('증권전달북 PDF 다운로드에 실패했습니다.', 'error')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-bold text-slate-900">증권전달북</h2>
          {loading && <span className="text-xs text-slate-500">불러오는 중</span>}
        </div>

        {templates.length > 0 ? (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {templates.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => {
                  setSelectedId(template.id)
                  clearPreview()
                }}
                className={`rounded-lg border p-4 text-left transition ${
                  String(selectedId) === String(template.id) ? 'border-sgez-primary bg-sky-50 ring-1 ring-sgez-primary/20' : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                <div className="font-semibold text-slate-900">{template.title}</div>
                {template.description && <div className="mt-1 text-sm text-slate-500">{template.description}</div>}
              </button>
            ))}
          </div>
        ) : (
          <div className="rounded border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500">
            다운로드 가능한 템플릿이 없습니다.
          </div>
        )}

        {selected && (
          <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(240px,0.7fr)]">
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">전달 코멘트</span>
              <textarea
                className="mt-1 min-h-[96px] w-full rounded border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-1 focus:ring-sgez-primary"
                value={comment}
                maxLength={500}
                onChange={(e) => {
                  setComment(e.target.value)
                  clearPreview()
                }}
                placeholder="템플릿에 코멘트 필드가 있을 때 PDF에 반영됩니다."
              />
            </label>

            <div>
              <div className="mb-1 text-xs font-semibold text-slate-700">출력 섹션</div>
              {selectedSections.length ? (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
                  {selectedSections.map((section) => (
                    <label key={section.key} className="flex cursor-pointer items-center gap-2 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm hover:bg-white">
                      <input
                        type="checkbox"
                        checked={sectionKeys.includes(section.key)}
                        onChange={() => toggleSection(section.key)}
                      />
                      <span className="font-semibold text-slate-800">{section.label}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">전체 페이지</div>
              )}
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={preview}
            disabled={!canRender || downloading}
            className="rounded border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            미리보기
          </button>
          <button
            type="button"
            onClick={download}
            disabled={!canRender || downloading}
            className="rounded bg-sgez-primary px-4 py-2 text-sm font-semibold text-white hover:bg-sgez-primary/90 disabled:opacity-50"
          >
            다운로드
          </button>
        </div>
      </div>

      {previewUrl && (
        <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
          <iframe title="증권전달북 미리보기" src={previewUrl} className="h-[640px] w-full rounded border border-slate-200" />
        </div>
      )}
    </div>
  )
}
